import { useMemo } from 'react';
import { format } from 'date-fns';
import { Space } from 'antd';
import { Button } from '@chakra-ui/react';

import SourceLogo from '@components/general/source-logo.component';
import { getColumnInputProps } from '@util/helpers/table.helpers';

export const useRecordingsColumns = ({
  onUpload,
  onViewSummary,
  uploadingRecords = new Set()
} = {}) => {
  const columns = useMemo(
    () => [
      {
        title: 'Type',
        dataIndex: 'type',
        width: 60,
        render: (_, record) => (
          <SourceLogo
            sourceType='Recording'
            source={record}
          />
        )
      },
      {
        title: 'Title',
        dataIndex: 'title',
        ...getColumnInputProps('title'),
        render: (title) => title,
        sorter: (a, b) => a.title?.localeCompare(b.title),
        sortDirections: ['descend', 'ascend']
      },
      {
        title: 'Start Date',
        dataIndex: 'start_at',
        render: (startAt) => {
          if (!startAt) return '-';
          const date = new Date(startAt);
          return format(date, 'MMM d yyyy h:mm a');
        },
        sorter: (a, b) => new Date(a.start_at) - new Date(b.start_at),
        sortDirections: ['descend', 'ascend']
      },
      {
        title: 'Action',
        dataIndex: 'action',
        width: 200,
        render: (_, record) => {
          const hasTranscript = !!record.transcript_cloud_key;
          const hasVideo = !!record.video_cloud_key;
          const canUpload = hasTranscript || hasVideo;
          const isUploading = uploadingRecords.has(record.id);
          const hasMeeting = !!record.meeting;

          return (
            <Space>
              <Button
                variant='reskinOutline'
                size='sm'
                isDisabled={!hasMeeting && !canUpload}
                isLoading={isUploading}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasMeeting && onViewSummary) {
                    onViewSummary(record);
                  } else if (onUpload && canUpload) {
                    onUpload(record);
                  }
                }}
              >
                {hasMeeting ? 'View Summary' : 'Upload to FORA'}
              </Button>
            </Space>
          );
        }
      }
    ],
    [onUpload, onViewSummary, uploadingRecords]
  );

  return columns;
};

export const filtersToRecordingsSearchBody = (filters) => {
  return {
    title: filters?.title?.[0],
    status: filters?.status?.[0],
    provider: filters?.provider?.[0]
  };
};
